/**
 * 夥伴詳情 — 班次配額分頁（AC-G-8）。
 *
 * 配額是「業主分多少座位給這個夥伴賣」。後端以 (partner_id, schedule_id) 為鍵
 * upsert，所以同一班次再設一次就是覆寫而非新增。
 *
 * 班次選擇要先選商品：後端 `GET /schedules` 需要 product_id，沒有跨商品的班次
 * 列表端點。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from './Button';
import { Field, ModalShell, errorTextStyle, inputStyle, useEscapeToClose } from './Modal';
import {
  listPartnerAllocations,
  listProductsLite,
  listSchedules,
  setPartnerAllocation,
  type PartnerAllocation,
} from '../lib/api-client';
import { formatDateTime } from '../lib/partner-format';

export function PartnerAllocationsTab({ partnerId }: { partnerId: string }): JSX.Element {
  const [editing, setEditing] = useState<PartnerAllocation | 'new' | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['partners.allocations', partnerId],
    queryFn: () => listPartnerAllocations(partnerId),
  });

  const items = data?.items ?? [];

  return (
    <div>
      <div className="page-head" style={{ marginBottom: 8 }}>
        <div />
        <div className="page-head__actions">
          <Button variant="primary" onClick={() => setEditing('new')}>
            + 設定班次配額
          </Button>
        </div>
      </div>

      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 12px' }}>
        指定各班次可分配給本夥伴銷售的座位數。同一班次重複設定為覆寫；
        調降時不會低於已使用數（由後端把關）。
      </p>

      {isLoading && <div>載入中…</div>}
      {error && <div className="error-banner">{(error as Error).message}</div>}

      {data && (
        <div className="table-card">
          <table>
            <thead>
              <tr>
                <th>班次 ID</th>
                <th style={{ textAlign: 'right' }}>配額</th>
                <th style={{ textAlign: 'right' }}>已使用</th>
                <th style={{ textAlign: 'right' }}>剩餘</th>
                <th>更新時間</th>
                <th style={{ width: 60 }}>動作</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty-cell">
                    尚未配額；此夥伴目前不能銷售任何班次。
                  </td>
                </tr>
              )}
              {items.map((a) => (
                <tr key={a.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{a.schedule_id}</td>
                  <td style={{ textAlign: 'right' }}>{a.allocated_qty}</td>
                  <td style={{ textAlign: 'right' }}>{a.used_qty}</td>
                  <td style={{ textAlign: 'right', fontWeight: a.remaining_qty === 0 ? 700 : 400 }}>
                    {a.remaining_qty}
                  </td>
                  <td style={{ fontSize: 12 }}>{formatDateTime(a.updated_at)}</td>
                  <td>
                    <button
                      onClick={() => setEditing(a)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#0b5c8a',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      調整
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <AllocationModal
          partnerId={partnerId}
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function AllocationModal({
  partnerId,
  existing,
  onClose,
}: {
  partnerId: string;
  existing: PartnerAllocation | null;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState('');
  const [scheduleId, setScheduleId] = useState(existing?.schedule_id ?? '');
  const [qty, setQty] = useState(String(existing?.allocated_qty ?? ''));

  const { data: products } = useQuery({
    queryKey: ['products.lite'],
    queryFn: listProductsLite,
    enabled: existing === null,
  });

  const { data: schedules } = useQuery({
    queryKey: ['schedules.forProduct', productId],
    queryFn: () => listSchedules(productId, { limit: 200 }),
    enabled: existing === null && productId.length > 0,
  });

  const mutation = useMutation({
    mutationFn: () => setPartnerAllocation(partnerId, scheduleId, Number(qty)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['partners.allocations', partnerId] });
      onClose();
    },
  });

  useEscapeToClose(onClose, !mutation.isPending);

  const qtyNum = Number(qty);
  const qtyInvalid = qty.trim() !== '' && (!Number.isInteger(qtyNum) || qtyNum < 0);
  const canSubmit =
    scheduleId.length > 0 && qty.trim() !== '' && !qtyInvalid && !mutation.isPending;

  return (
    <ModalShell onBackdropClick={mutation.isPending ? undefined : onClose} width={560}>
      <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>
        {existing ? '調整班次配額' : '設定班次配額'}
      </h2>

      {existing ? (
        <Field label="班次" hint="調整既有配額時不可換班次；請改為新增一筆">
          <input
            value={existing.schedule_id}
            readOnly
            style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12, background: '#f9fafb' }}
          />
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
            目前已使用 {existing.used_qty} 席，配額不可低於此數。
          </div>
        </Field>
      ) : (
        <>
          <Field label="商品" hint="先選商品才能列出其班次">
            <select
              value={productId}
              onChange={(e) => {
                setProductId(e.target.value);
                setScheduleId('');
              }}
              style={inputStyle}
            >
              <option value="">— 請選擇 —</option>
              {(products?.items ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name_i18n['zh-TW'] ?? p.sku}
                </option>
              ))}
            </select>
          </Field>

          <Field label="班次">
            <select
              value={scheduleId}
              onChange={(e) => setScheduleId(e.target.value)}
              style={inputStyle}
              disabled={!productId}
            >
              <option value="">— 請選擇 —</option>
              {(schedules?.items ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {formatDateTime(s.departure_at)}（容量 {s.capacity}、已佔 {s.reserved_count}）
                  {s.weather_cancelled ? ' · 已停航' : ''}
                </option>
              ))}
            </select>
            {productId && (schedules?.items.length ?? 0) === 0 && (
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>此商品尚無班次。</div>
            )}
          </Field>
        </>
      )}

      <Field label="配額席次" hint="0 = 收回配額（不刪除紀錄）">
        <input
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          inputMode="numeric"
          placeholder="20"
          style={inputStyle}
        />
        {qtyInvalid && <div style={errorTextStyle}>請輸入 0 以上的整數</div>}
      </Field>

      {mutation.isError && (
        <div className="error-banner" style={{ marginBottom: 12 }}>
          {(mutation.error as Error).message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
          取消
        </Button>
        <Button variant="primary" onClick={() => mutation.mutate()} disabled={!canSubmit}>
          {mutation.isPending ? '儲存中…' : '儲存'}
        </Button>
      </div>
    </ModalShell>
  );
}
